import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, ScanLine, CheckCircle2, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function BulkOrders() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [items, setItems] = useState([]); // { ...item, selected, ... }
  const [konfidenz, setKonfidenz] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  const choose = (f) => {
    setFile(f);
    setItems([]); setKonfidenz(null);
    if (!f) { setPreview(null); return; }
    if (f.type === "application/pdf" || f.name?.toLowerCase().endsWith(".pdf")) {
      setPreview({ pdf: true, name: f.name });
    } else {
      setPreview({ pdf: false, url: URL.createObjectURL(f) });
    }
  };

  const analyze = async () => {
    if (!file) { toast.error("Bitte zuerst ein Bild auswählen"); return; }
    setAnalyzing(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/ai/bulk-screenshot-analysis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const annotated = (data.items || []).map((it) => ({ ...it, selected: !it.already_exists }));
      setItems(annotated);
      setKonfidenz(data.konfidenz);
      toast.success(`${data.count_total} Aufträge erkannt (${data.count_new} neu, ${data.count_existing} bereits vorhanden)`);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const toggle = (idx) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it));
  const toggleAll = (val) => setItems((arr) => arr.map((it) => ({ ...it, selected: val })));

  const save = async () => {
    const selected = items.filter((it) => it.selected);
    if (selected.length === 0) { toast.error("Keine Aufträge ausgewählt"); return; }
    setSaving(true);
    try {
      const payload = {
        items: selected.map((it) => ({
          auftragsnummer: it.auftragsnummer,
          kls_id: it.kls_id,
          ausfuehrungsdatum: it.datum,
          auftragstyp: it.auftragstyp || "FTTH",
          plz: it.plz,
          ort: it.ort,
          strasse: it.strasse,
          terminierungsdatum: it.terminierungsdatum,
        })),
        update_existing: updateExisting && isAdmin,
      };
      const { data } = await api.post("/orders/bulk", payload);
      toast.success(`${data.count_created} angelegt · ${data.count_updated} aktualisiert · ${data.count_skipped} übersprungen`);
      setItems([]); setFile(null); setPreview(null);
      nav("/orders");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openExisting = (it) => {
    // Find order id by auftragsnummer
    api.get("/orders", { params: { q: it.auftragsnummer } }).then((r) => {
      const found = r.data.find((o) => o.auftragsnummer === it.auftragsnummer);
      if (found) nav(`/orders/${found.id}`);
    });
  };

  const conf = konfidenz;
  const confColor = conf === "hoch" ? "text-emerald-700" : conf === "mittel" ? "text-amber-700" : "text-red-700";
  const selectedCount = items.filter((it) => it.selected).length;
  const newCount = items.filter((it) => !it.already_exists).length;
  const allSelected = items.length > 0 && items.every((it) => it.selected);

  return (
    <div data-testid="bulk-orders">
      <PageHeader
        title="Sammel-Aufträge"
        subtitle="Mehrere Aufträge auf einmal aus einem KASYS-Listenscreenshot übernehmen"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upload */}
        <div className="bg-white border rounded-md p-5" style={{ borderColor: "#E2E8F0" }}>
          <label
            data-testid="bulk-dropzone"
            className="block border-2 border-dashed rounded-md p-8 text-center cursor-pointer hover:border-[#0A2540] transition-colors"
            style={{ borderColor: "#CBD5E1", background: "#F8FAFC" }}
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
              className="hidden"
              onChange={(e) => choose(e.target.files?.[0] || null)}
              data-testid="bulk-file-input"
            />
            {!preview ? (
              <div className="text-slate-500">
                <Upload className="h-8 w-8 mx-auto mb-3 text-slate-400" />
                <div className="font-semibold text-[#0A2540]">Sammel-Datei wählen</div>
                <div className="text-xs mt-1">PNG · JPEG · WEBP · PDF — mehrere Aufträge auf einem Bild/PDF</div>
              </div>
            ) : preview.pdf ? (
              <div className="py-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-md mb-3" style={{ background: "#FEE2E2" }}>
                  <span className="text-xs font-bold text-red-700">PDF</span>
                </div>
                <div className="text-sm font-semibold truncate max-w-[280px] mx-auto">{preview.name}</div>
                <div className="text-xs text-slate-500 mt-1">PDF mit mehreren Seiten möglich</div>
              </div>
            ) : (
              <img src={preview.url} alt="Vorschau" className="max-h-72 mx-auto rounded" />
            )}
          </label>
          <div className="mt-4 flex gap-2">
            <Button
              data-testid="bulk-analyze-btn"
              className="h-12 px-6 bg-[#0A2540] hover:bg-[#113A63] text-white"
              onClick={analyze}
              disabled={analyzing || !file}
            >
              <ScanLine className="h-4 w-4 mr-2" />{analyzing ? "Analysiert…" : "Sammel-Analyse starten"}
            </Button>
            {file && <Button variant="outline" className="h-12" onClick={() => choose(null)}>Zurücksetzen</Button>}
          </div>
        </div>

        {/* Übersicht */}
        <div className="bg-white border rounded-md p-5" style={{ borderColor: "#E2E8F0" }}>
          <h3 className="font-bold mb-3">Übersicht</h3>
          {!konfidenz && <div className="text-sm text-slate-400 py-12 text-center">Noch keine Analyse</div>}
          {konfidenz && (
            <>
              <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3 ${confColor}`}>
                {conf === "hoch" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} Konfidenz: {conf}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Box label="Erkannt" val={items.length} />
                <Box label="Neu" val={newCount} accent="#DCFCE7" />
                <Box label="Bereits vorhanden" val={items.length - newCount} accent="#FEF3C7" />
              </div>
              {isAdmin && items.some((it) => it.already_exists) && (
                <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
                  <Checkbox checked={updateExisting} onCheckedChange={setUpdateExisting} data-testid="bulk-update-existing" />
                  Bestehende Aufträge mit neuen Daten aktualisieren (Admin)
                </label>
              )}
            </>
          )}
        </div>
      </div>

      {/* Vorschau-Tabelle */}
      {items.length > 0 && (
        <div className="bg-white border rounded-md overflow-hidden mt-5" style={{ borderColor: "#E2E8F0" }} data-testid="bulk-preview">
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
            <h3 className="font-bold">Vorschau ({selectedCount} ausgewählt)</h3>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={allSelected} onCheckedChange={(v) => toggleAll(!!v)} data-testid="bulk-select-all" />
              Alle auswählen
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 w-12"></th>
                  <th className="text-left px-4 py-3">Datum</th>
                  <th className="text-left px-4 py-3">Auftragsnr.</th>
                  <th className="text-left px-4 py-3">KLS-ID</th>
                  <th className="text-left px-4 py-3">Typ</th>
                  <th className="text-left px-4 py-3">PLZ/Ort</th>
                  <th className="text-left px-4 py-3">Termin</th>
                  <th className="text-left px-4 py-3">Status Prüfung</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: "#E2E8F0", background: it.already_exists ? "#FFFBEB" : "transparent" }} data-testid={`bulk-row-${it.auftragsnummer}`}>
                    <td className="px-4 py-2.5">
                      <Checkbox checked={it.selected} onCheckedChange={() => toggle(i)} data-testid={`bulk-select-${it.auftragsnummer}`} />
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{it.datum || "—"}</td>
                    <td className="px-4 py-2.5 font-semibold">{it.auftragsnummer}</td>
                    <td className="px-4 py-2.5 mono">{it.kls_id || "—"}</td>
                    <td className="px-4 py-2.5">{it.auftragstyp || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{[it.plz, it.ort, it.strasse].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-2.5 tabular-nums">{it.terminierungsdatum || "—"}</td>
                    <td className="px-4 py-2.5">
                      {it.already_exists ? (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border" style={{ background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A" }}>
                          Bereits vorhanden
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border" style={{ background: "#DCFCE7", color: "#166534", borderColor: "#BBF7D0" }}>
                          Neu
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {it.already_exists && (
                        <button className="text-xs font-semibold text-[#0A2540] hover:underline" onClick={() => openExisting(it)} data-testid={`bulk-open-${it.auftragsnummer}`}>
                          Öffnen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-4 border-t flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
            <div className="text-xs text-slate-500">
              Vor dem Speichern bitte alle Aufträge prüfen. Fehlende Pflichtfelder (Mitarbeiter, Fahrzeug, Telekom Konto) können später manuell ergänzt werden.
            </div>
            <Button
              className="h-12 px-6 bg-[#0A2540] hover:bg-[#113A63] text-white"
              onClick={save}
              disabled={saving || selectedCount === 0}
              data-testid="bulk-save-btn"
            >
              <Save className="h-4 w-4 mr-2" /> {saving ? "Speichert…" : `${selectedCount} Aufträge speichern`}
            </Button>
          </div>
        </div>
      )}

      <ActionBar hideNext onPrint={() => window.print()} />
    </div>
  );
}

function Box({ label, val, accent }) {
  return (
    <div className="border rounded-md p-3 text-center" style={{ borderColor: "#E2E8F0", background: accent || "#F8FAFC" }}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{val}</div>
    </div>
  );
}
